import { useEffect, useMemo, useRef } from 'react';
import { useAppStore } from '../../app/appStore';

export type HistoryLayer = {
  /** 同一时刻的层栈里唯一即可；用「页面.用途」的写法，例如 `room.bet`。 */
  id: string;
  open: boolean;
  /** 用户按了系统返回键（或它下面的层被关掉、把它一起带走）时调用。 */
  onClose: () => void;
};

/**
 * 把一页上的浮层挂进浏览器历史，让系统返回键关掉最上面那一层、
 * 而不是关掉它们下面那一页。
 *
 * <p>一页只调一次，把该页所有浮层按**从下到上**的叠放顺序传进来：
 *
 * <pre>
 *   useHistoryLayers([
 *     { id: 'room.plus', open: plusOpen, onClose: () =&gt; setPlusOpen(false) },
 *     { id: 'room.bet', open: betOpen, onClose: () =&gt; setBetOpen(false) },
 *     { id: 'room.basket', open: basketOpen, onClose: () =&gt; setBasketOpen(false) },
 *   ]);
 * </pre>
 *
 * <p>为什么是整组、而不是每层各自调一次：「同一次点击里换一层」是这套 UI 的常态
 * （`+` 面板换成我的投注、历史抽屉换成某期详情）。逐层做的话，关旧层的
 * `history.go(-1)` 和开新层的 `pushState` 会在同一 tick 里互相打架，结果依赖
 * 浏览器怎么排队。整组对账时，那种情况被识别成一次「换层」，只改写当前条目。
 *
 * <p>刻意不做的事：前进键**不会**把层重新打开。层的内容（选中哪一注、哪一期）
 * 从来不在 URL 里，「恢复」出来的只会是个空壳。
 */
export function useHistoryLayers(layers: HistoryLayer[]) {
  const layerStack = useAppStore((state) => state.layerStack);
  const syncLayers = useAppStore((state) => state.syncLayers);

  // Callers build this array inline every render; without a stable key the sync
  // effect would re-run on every tick of the room's 1s clock.
  const openIds = layers.filter((layer) => layer.open).map((layer) => layer.id);
  const openKey = openIds.join(' ');
  const openList = useMemo(() => (openKey ? openKey.split(' ') : []), [openKey]);

  const layersRef = useRef(layers);
  layersRef.current = layers;

  useEffect(() => {
    syncLayers(openList);
  }, [openList, syncLayers]);

  useEffect(() => {
    // History moved out from under the UI: system back, or a lower layer closing
    // and taking the ones above it. The entries are already gone, so only the
    // components need catching up.
    for (const layer of layersRef.current) {
      if (layer.open && !layerStack.includes(layer.id)) layer.onClose();
    }
  }, [layerStack]);
}
